import { useMemo, useState } from "react";
import type { Dataset, Customer, ARByCustomer, CreditEvent } from "../lib/types";
import type { Navigate } from "../App";
import { exportCsv } from "../lib/export";
import { showToast } from "../components/Toast";
import { customerLookup, dsoOverall, dsoTrend, dsoDriftEnt, customersInCriticalTier, arOver90Pct, payCycleBoxplots, scoreRisk } from "../lib/derive";
import { jitter } from "../lib/liveClock";
import { drawDsoStacked, drawAgingBars, drawPayCycleBoxplots } from "../lib/charts";
import KpiTile from "../components/KpiTile";
import Card from "../components/Card";
import Chart from "../components/Chart";
import ModelExplainModal, { ExplainButtons } from "../components/ModelExplainModal";
import { MODEL_EXPLAIN, type ExplainMode } from "../lib/modelExplain";
import { I } from "../lib/infoText";

type TierFilter = "all" | "DIRECT" | "FIRST_TIER" | "SUB_TO_SUB";
type RiskFilter = "all" | "critical" | "high" | "med";

function fmtMoneyM(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

function tierBadge(tier: string) {
  if (tier === "DIRECT") return <span className="tier-badge tier-direct">Direct</span>;
  if (tier === "FIRST_TIER") return <span className="tier-badge tier-ft">First-tier</span>;
  if (tier === "SUB_TO_SUB") return <span className="tier-badge tier-sts">Sub-to-sub</span>;
  return <span className="tier-badge">{tier}</span>;
}


interface Props { data: Dataset; navigate?: Navigate; tick?: number }

export default function ARCustomerRisk({ data, navigate, tick = 0 }: Props) {
  const [tier, setTier] = useState<TierFilter>("all");
  const [risk, setRisk] = useState<RiskFilter>("all");
  const [explainMode, setExplainMode] = useState<ExplainMode | null>(null);
  const lookup = useMemo(() => customerLookup(data.customers), [data]);
  const dsoEntRaw = useMemo(() => dsoOverall(data.ar_by_customer), [data]);
  const trend = useMemo(() => dsoTrend(data, 12), [data]);
  const dsoDriftRaw = useMemo(() => dsoDriftEnt(data), [data]);
  const critCount = useMemo(() => customersInCriticalTier(data), [data]);
  const arOver90Raw = useMemo(() => arOver90Pct(data), [data]);
  const boxplots = useMemo(() => payCycleBoxplots(data), [data]);

  const dsoEnt = useMemo(() => jitter(dsoEntRaw, 0.005, tick + 11), [dsoEntRaw, tick]);
  const dsoDrift = useMemo(() => ({ ...dsoDriftRaw, value: jitter(dsoDriftRaw.value, 0.05, tick + 12) }), [dsoDriftRaw, tick]);
  const arOver90 = useMemo(() => jitter(arOver90Raw, 0.01, tick + 13), [arOver90Raw, tick]);

  const totalArRaw = data.ar_by_customer.reduce((a, c) => a + c.ar_balance, 0);
  const totalAr = useMemo(() => jitter(totalArRaw, 0.004, tick + 14), [totalArRaw, tick]);
  const overdue = (data.ar_aging["91-120"] ?? 0) + (data.ar_aging["120+"] ?? 0);
  const overduePct = totalAr > 0 ? overdue / totalAr : 0;
  void overduePct;

  const arRows = useMemo(
    () =>
      data.ar_by_customer
        .map((a) => {
          const c = lookup.get(a.account_num);
          const r = scoreRisk(a, c, data.credit_events);
          return { ar: a, c, score: r.score, risk: r.risk };
        })
        .filter((r) => (tier === "all" ? true : r.c?.tier === tier))
        .filter((r) => {
          if (risk === "all") return true;
          if (risk === "critical") return r.risk === "critical";
          if (risk === "high") return r.risk === "high";
          if (risk === "med") return r.risk === "medium";
          return true;
        })
        .sort((a, b) => b.score - a.score),
    [data, lookup, tier, risk]
  );

  const watchCount = arRows.filter((r) => r.score >= 50).length;
  void watchCount;

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">AR Anomaly &amp; Customer Risk Intelligence</h1>
          <p className="page-sub">
            Tier-driven cash-cycle drift surfaced 30–45 days before AR aging shows it. Plus per-customer behavioral
            baselines and external distress signals.
          </p>
        </div>
        <div className="page-controls">
          <div className="seg">
            {(["all", "DIRECT", "FIRST_TIER", "SUB_TO_SUB"] as TierFilter[]).map((t) => (
              <button key={t} className={`seg-btn${tier === t ? " active" : ""}`} onClick={() => setTier(t)}>
                {t === "all" ? "All tiers" : t === "DIRECT" ? "Direct" : t === "FIRST_TIER" ? "First-tier" : "Sub-to-sub"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="kpi-grid four">
        <KpiTile
          id="K-013"
          band="critical"
          label="DSO by Tier"
          sub="weighted blended"
          value={`${dsoEnt.toFixed(1)}d`}
          delta={1.2}
          deltaUnit="d"
          deltaWindow="WoW"
          direction="warn"
          spark={[55.1, 55.4, 55.7, 55.9, 56.2, 56.5, 56.7, 56.8, 57.0, 57.3, 57.5, 57.7, 57.9, 58.0, 58.2, dsoEnt]}
          info={I.K013}
        />
        <KpiTile
          id="K-014"
          band={dsoDrift.value > 5 ? "critical" : "high"}
          label="DSO Drift Enterprise"
          sub="vs trailing-12 baseline"
          value={`${dsoDrift.value > 0 ? "+" : ""}${dsoDrift.value.toFixed(1)}d`}
          delta={0.6}
          deltaUnit="d"
          deltaWindow="WoW"
          direction="warn"
          spark={dsoDrift.spark}
          info={I.K014}
        />
        <KpiTile
          id="K-028"
          band="critical"
          label="Customers in Critical Tier"
          sub="K-028 risk classification"
          value={String(critCount)}
          delta={2}
          deltaUnit=""
          deltaWindow="WoW"
          direction="bad"
          spark={[3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 5, 6, 6, 6, critCount]}
          info={I.K028Crit}
          ai
        />
        <KpiTile
          id="K-016"
          band={arOver90 > 8 ? "critical" : arOver90 > 5 ? "high" : "good"}
          label="AR > 90d"
          sub="% of total AR"
          value={`${arOver90.toFixed(1)}%`}
          delta={0.8}
          deltaUnit="pp"
          deltaWindow="MoM"
          direction="warn"
          spark={[5.4, 5.6, 5.7, 5.8, 5.9, 6.0, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.6, 6.7, 6.7, arOver90]}
          info={I.K016}
        />
      </div>

      <div className="row-2">
        <Card span2 eyebrow="K-013 · K-014" title="Enterprise DSO with tier decomposition (12m)"
          info={I.l2Dso}
          meta={
            <div className="legend">
              <span><span className="lg" style={{ background: "var(--c-direct)" }} />Direct</span>
              <span><span className="lg" style={{ background: "var(--c-ft)" }} />First-tier</span>
              <span><span className="lg" style={{ background: "var(--c-sts)" }} />Sub-to-sub</span>
            </div>
          }
        >
          <Chart viewBox="0 0 920 200" draw={(svg) => drawDsoStacked(svg, trend)} deps={[trend]} />
        </Card>

        <Card eyebrow="K-024 · aging" title="AR aging buckets" info={I.l2Aging}>
          <Chart viewBox="0 0 460 200" draw={(svg) => drawAgingBars(svg, data.ar_aging)} deps={[data.ar_aging]} />
        </Card>
      </div>

      <div className="row-2">
        <Card span2 eyebrow="K-027 · per tier" title="Pay-cycle distribution by tier" info={I.l2PayCycle}>
          <Chart viewBox="0 0 920 200" draw={(svg) => drawPayCycleBoxplots(svg, boxplots)} deps={[boxplots]} />
        </Card>

        <Card eyebrow="K-014 · drift" title="DSO drift signal · 16 weeks" info={I.l2DsoDrift}
          foot={
            <div className="callout small">
              {dsoDrift.value > 5 ? (
                <><span className="callout-tag warn">ALERT</span>Drift {dsoDrift.value.toFixed(1)}d sustained &gt;5d for 2+ weeks.</>
              ) : (
                <>Drift currently {dsoDrift.value > 0 ? "+" : ""}{dsoDrift.value.toFixed(1)}d — within tolerance.</>
              )}
            </div>
          }
        >
          <div style={{ padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: 32, fontWeight: 600, color: "var(--t1)" }}>
              {dsoDrift.value > 0 ? "+" : ""}{dsoDrift.value.toFixed(1)}d
            </div>
            <div style={{ fontSize: 11, color: "var(--t3)" }}>vs trailing-12 baseline</div>
            <div style={{ width: "100%", height: 100 }}>
              <svg viewBox="0 0 460 100" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
                {dsoDrift.spark.map((v, i) => {
                  const max = Math.max(...dsoDrift.spark.map(Math.abs)) || 1;
                  const x = 20 + (i / (dsoDrift.spark.length - 1)) * 420;
                  const y0 = 50;
                  const y = y0 - (v / max) * 35;
                  return <circle key={i} cx={x} cy={y} r={3} fill={v > 0 ? "var(--warning)" : "var(--good)"} />;
                })}
                <line x1="20" x2="440" y1="50" y2="50" stroke="var(--axis)" strokeDasharray="3,3" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      <div className="row-2">
        <Card
          span2
          eyebrow="K-028"
          title="Customer Risk Watchlist"
          info={I.l2Watch}
          ai
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">Precision</span><span className="ms-val">0.92</span></div>
              <div><span className="ms-label">Recall</span><span className="ms-val">0.86</span></div>
              <div><span className="ms-label">F1</span><span className="ms-val">0.89</span></div>
              <div><span className="ms-label">AUC-ROC</span><span className="ms-val">0.94</span></div>
              <div><span className="ms-label">Threshold</span><span className="ms-val">75</span></div>
              <div><span className="ms-label">Calibration</span><span className="ms-val ok">PASS</span></div>
            </div>
          }
          meta={
            <>
              <div className="seg sm">
                {(["all", "critical", "high", "med"] as RiskFilter[]).map((r) => (
                  <button key={r} className={`seg-btn${risk === r ? " active" : ""}`} onClick={() => setRisk(r)}>
                    {r === "all" ? "All" : r === "med" ? "Medium" : r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
              <button
                className="card-export"
                onClick={() => {
                  exportCsv(
                    arRows,
                    [
                      { header: "Customer", value: (r) => r.c?.customer_name ?? r.ar.account_num },
                      { header: "Account",  value: (r) => r.ar.account_num },
                      { header: "Tier",     value: (r) => r.c?.tier_display ?? "" },
                      { header: "AR balance", value: (r) => r.ar.ar_balance.toFixed(2) },
                      { header: "Last pay-cycle (d)", value: (r) => r.ar.last_pay_cycle_days ?? "" },
                      { header: "Paydex",   value: (r) => r.c?.credit_paydex_baseline ?? "" },
                      { header: "Open events", value: (r) => data.credit_events.filter((e) => e.customer_account === r.ar.account_num).length },
                      { header: "Risk score", value: (r) => r.score },
                      { header: "Risk band",  value: (r) => r.risk },
                    ],
                    `watchlist-${data.meta.asOf}`
                  );
                  showToast("Watchlist exported (CSV)", "success");
                }}
              >
                ⬇ Export CSV
              </button>
              <ExplainButtons onOpen={setExplainMode} />
            </>
          }
        >
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Tier</th>
                <th>AR balance</th>
                <th>Last pay-cycle</th>
                <th>Paydex</th>
                <th>Open events</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {arRows.slice(0, 10).map((r) => {
                const evCount = data.credit_events.filter((e) => e.customer_account === r.ar.account_num).length;
                return (
                  <tr
                    key={r.ar.account_num}
                    onClick={() => navigate?.("customer-360", { customerAccount: r.ar.account_num })}
                    style={{ cursor: navigate ? "pointer" : undefined }}>
                    <td className="col-strong">{r.c?.customer_name ?? r.ar.account_num}</td>
                    <td>{r.c ? tierBadge(r.c.tier) : "—"}</td>
                    <td className="col-num">{fmtMoneyM(r.ar.ar_balance)}</td>
                    <td className="col-num">{r.ar.last_pay_cycle_days != null ? `${Math.round(r.ar.last_pay_cycle_days)}d` : "—"}</td>
                    <td className="col-num">{r.c?.credit_paydex_baseline ?? "—"}</td>
                    <td className="col-num">{evCount}</td>
                    <td>
                      <span className={`risk-pill risk-${r.risk}`}>
                        <span className="risk-dot" />
                        {r.risk.toUpperCase()} · {r.score}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {arRows.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: "16px", color: "var(--t4)" }}>No customers match this filter.</td></tr>
              )}
            </tbody>
          </table>
          {arRows.length > 10 && (
            <div style={{ padding: "8px 16px", fontSize: 11, color: "var(--t4)", textAlign: "right" }}>
              showing 10 of {arRows.length} customers · +{arRows.length - 10} more
            </div>
          )}
        </Card>

        <Card eyebrow="External" title="Distress events · last 90d" info={I.l2Events}>
          <ul className="event-feed">
            {data.credit_events.slice(0, 10).map((e) => {
              const sev = e.severity.toLowerCase();
              return (
                <li key={e.credit_event_id} className="event">
                  <div className={`event-dot ${sev}`} />
                  <div>
                    <div className="event-title">
                      <strong>{lookup.get(e.customer_account)?.customer_name ?? e.customer_account}</strong> · {e.event_type}
                    </div>
                    <div className="event-meta">
                      <span className="event-source">{e.source}</span>
                      <span>{e.event_date}</span>
                    </div>
                  </div>
                </li>
              );
            })}
            {data.credit_events.length === 0 && (
              <li className="event"><div /><div className="event-title" style={{ color: "var(--t4)" }}>No distress events.</div></li>
            )}
          </ul>
        </Card>
      </div>

      {explainMode && (
        <ModelExplainModal spec={MODEL_EXPLAIN.l2} mode={explainMode} onClose={() => setExplainMode(null)} />
      )}
    </section>
  );
}
